import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { Editor } from '@tiptap/core';
import type { EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import FileHandler from '@tiptap/extension-file-handler';
import { compressImage } from '../lib/imageCompression';
import { validateImageFile } from '../lib/imageValidation';
import { uploadToStorage } from '../lib/storageUpload';
import {
  BlogImage,
  updateImageAttrsBySrc,
  removeImageBySrc,
  removeBlobImages,
  setImageAttrsAt,
  removeImageAt,
} from '../lib/blogImageExtension';
import {
  BlogImageGroup,
  IMAGE_GROUP_MAX,
  appendImagesToGroup,
  defaultColumnsFor,
  findImageGroupAt,
  insertImageGroup,
  moveCursorAfterImage,
  normalizeImageGroups,
} from '../lib/blogImageGroupExtension';

// Waar de foto's heen gaan: bij een bestaand blog onder het blog, tijdens de wizard in de inline-map van de sessie.
export type EditorImageTarget =
  | { context: 'blog'; blogId: string }
  | { context: 'wizard-inline'; sessionId: string };

// De editor bestaat eerder dan de hook; via deze ref bereikt de editor-config de functies van de hook.
export interface EditorImageHandlers {
  insertFiles: (files: File[], pos?: number) => void;
  clickOn: (view: EditorView, node: PMNode, nodePos: number) => boolean;
}

export const noEditorImageHandlers: EditorImageHandlers = {
  insertFiles: () => {},
  clickOn: () => false,
};

// Dezelfde extensies voor beide editors: beeld, beeldgroep en bestanden slepen/plakken.
export function blogImageExtensions(handlersRef: MutableRefObject<EditorImageHandlers>) {
  return [
    BlogImage.configure({
      inline: false,
      allowBase64: false,
      HTMLAttributes: {
        class: 'max-w-full h-auto rounded-lg',
      },
    }),
    BlogImageGroup,
    FileHandler.configure({
      onDrop: (_currentEditor, files, pos) => handlersRef.current.insertFiles(files, pos),
      onPaste: (_currentEditor, files) => handlersRef.current.insertFiles(files),
    }),
  ];
}

// Voor editorProps.handleClickOn.
export function editorImageClickHandler(handlersRef: MutableRefObject<EditorImageHandlers>) {
  return (view: EditorView, _pos: number, node: PMNode, nodePos: number) =>
    handlersRef.current.clickOn(view, node, nodePos);
}

export interface EditorImagesOptions {
  editor: Editor | null;
  userId: string | null | undefined;
  target: EditorImageTarget | null;
  handlersRef: MutableRefObject<EditorImageHandlers>;
  onError: (message: string) => void; // lege string wist de melding
  onChanged: () => void; // bijvoorbeeld scheduleAutoSave
}

export interface ImageModalState {
  open: boolean;
  src: string;
  alt: string;
  caption: string;
  pos: number | null;
}

export interface EditorImageToolsProps {
  editor: Editor | null;
  fileInputRef: MutableRefObject<HTMLInputElement | null>;
  groupFileInputRef: MutableRefObject<HTMLInputElement | null>;
  onImageFiles: (files: File[]) => void;
  onGroupFiles: (files: File[]) => void;
  imageModal: ImageModalState;
  setImageAlt: (value: string) => void;
  setImageCaption: (value: string) => void;
  onImageSave: () => void;
  onImageRemove: () => void;
  onImageClose: () => void;
  imageGroupPos: number | null;
  onGroupClose: () => void;
  onGroupAddFiles: (groupPos: number) => void;
}

export interface EditorImages {
  openImagePicker: () => void;
  openGroupPicker: () => void;
  pendingUploads: () => number;
  // Vóór opslaan: dode blob-nodes weg en groepen normaliseren. false zolang er nog uploads lopen (dan niet opslaan).
  prepareForSave: () => boolean;
  toolsProps: EditorImageToolsProps;
}

const closedImageModal: ImageModalState = { open: false, src: '', alt: '', caption: '', pos: null };

export function useEditorImages({ editor, userId, target, handlersRef, onError, onChanged }: EditorImagesOptions): EditorImages {
  const [imageModal, setImageModal] = useState<ImageModalState>(closedImageModal);
  const [imageGroupPos, setImageGroupPos] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const groupFileInputRef = useRef<HTMLInputElement | null>(null);
  // null = nieuwe groep op de cursor; een getal = foto's toevoegen aan de groep op die positie.
  const groupFileTargetRef = useRef<number | null>(null);
  const pendingUploadsRef = useRef(0);

  const ready = Boolean(editor && userId && target);

  // Upload van één beeld dat al als blob-voorvertoning in het document staat (los of in een groep).
  const uploadImageNode = async (file: File, blobUrl: string) => {
    if (!editor || !userId || !target) return;
    pendingUploadsRef.current += 1;
    try {
      const compressed = await compressImage(file);
      const result = await uploadToStorage(
        compressed.blob,
        userId,
        target.context,
        target.context === 'wizard-inline' ? { sessionId: target.sessionId } : { blogId: target.blogId }
      );
      updateImageAttrsBySrc(editor, blobUrl, { src: result.publicUrl, uploading: false }, { addToHistory: false });
    } catch (err) {
      console.error('Error uploading image:', err);
      removeImageBySrc(editor, blobUrl, { addToHistory: false });
      // Valt een groep hierdoor terug op één of nul beelden, dan meteen opruimen.
      normalizeImageGroups(editor);
      onError(`We konden ${file.name} niet verwerken. Probeer een JPG-, PNG- of WebP-bestand, of controleer je verbinding.`);
    } finally {
      URL.revokeObjectURL(blobUrl);
      pendingUploadsRef.current -= 1;
      // Is het scherm intussen verlaten, dan geen autosave meer inplannen.
      if (!editor.isDestroyed) onChanged();
    }
  };

  const insertImageFromFile = (file: File, pos?: number) => {
    if (!editor || !ready) return;
    // insertContent vervangt de selectie: staat de cursor als node-selectie op een foto (los of in een
    // groep), dan zou de nieuwe foto die bestaande foto overschrijven. Zet hem in dat geval vlak
    // erachter, zodat een foto die je op een groep plakt of sleept in díé groep terechtkomt.
    if (typeof pos !== 'number' && editor.state.selection instanceof NodeSelection) pos = editor.state.selection.to;
    const blobUrl = URL.createObjectURL(file);
    const imageNode = { type: 'image', attrs: { src: blobUrl, uploading: true } };
    if (typeof pos === 'number') {
      editor.chain().focus().insertContentAt(pos, imageNode).run();
    } else {
      editor.chain().focus().insertContent(imageNode).run();
    }
    // Anders blijft de nieuwe foto als node-selectie staan en wist de eerste toetsaanslag hem.
    moveCursorAfterImage(editor, blobUrl);
    void uploadImageNode(file, blobUrl);
  };

  // Zelfde validatie voor losse beelden en beeldgroepen; zet de foutmelding en geeft de bruikbare bestanden terug.
  const filterValidImageFiles = (files: File[]): File[] => {
    const validations = files.map((file) => ({ file, result: validateImageFile(file) }));
    const invalidMessages = validations
      .map((v) => v.result)
      .filter((result): result is { ok: false; message: string } => !result.ok)
      .map((result) => result.message);
    onError(invalidMessages.length > 0 ? Array.from(new Set(invalidMessages)).join(' ') : '');
    return validations.filter((v) => v.result.ok).map((v) => v.file);
  };

  const insertImagesFromFiles = (files: File[], pos?: number) => {
    const validFiles = filterValidImageFiles(files);
    const ordered = typeof pos === 'number' ? [...validFiles].reverse() : validFiles;
    ordered.forEach((file) => insertImageFromFile(file, pos));
  };

  // Beeldgroep: nieuw op de cursor (targetPos null) of foto's toevoegen aan de groep op targetPos.
  const insertImageGroupFromFiles = (files: File[], targetPos: number | null) => {
    if (!editor || !ready) return;
    const validFiles = filterValidImageFiles(files);
    if (validFiles.length === 0) return;
    if (targetPos === null && validFiles.length === 1) {
      insertImageFromFile(validFiles[0]);
      return;
    }

    const existing = targetPos === null ? 0 : (findImageGroupAt(editor, targetPos)?.node.childCount ?? 0);
    const room = IMAGE_GROUP_MAX - existing;
    if (room <= 0) {
      onError(`Een beeldgroep bevat maximaal ${IMAGE_GROUP_MAX} foto's.`);
      return;
    }
    const chosen = validFiles.slice(0, room);
    if (validFiles.length > room) {
      onError(`Een beeldgroep bevat maximaal ${IMAGE_GROUP_MAX} foto's; de eerste ${room} zijn geplaatst.`);
    }

    const entries = chosen.map((file) => ({ file, blobUrl: URL.createObjectURL(file) }));
    const images = entries.map((entry) => ({ src: entry.blobUrl, uploading: true }));
    const placed = targetPos === null
      ? insertImageGroup(editor, images, defaultColumnsFor(images.length))
      : appendImagesToGroup(editor, targetPos, images);
    if (!placed) {
      entries.forEach((entry) => URL.revokeObjectURL(entry.blobUrl));
      onError('De beeldgroep is niet meer gevonden. Sluit het venster en probeer het opnieuw.');
      return;
    }
    // Nieuwe groep: cursor erachter, anders wist de eerste toetsaanslag de hele groep. Focus zonder
    // positie laat die selectie staan. Bij toevoegen aan een bestaande groep blijft het venster open.
    if (targetPos === null) {
      moveCursorAfterImage(editor, images[0].src);
      editor.commands.focus();
    }
    entries.forEach((entry) => void uploadImageNode(entry.file, entry.blobUrl));
  };

  // Klik op een groep (of een foto erin) opent het groepsvenster, ook tijdens het uploaden;
  // een los beeld opent het beeldvenster zodra de upload klaar is.
  const clickOn = (view: EditorView, node: PMNode, nodePos: number): boolean => {
    if (node.type.name === 'imageGroup') {
      setImageGroupPos(nodePos);
      return true;
    }
    if (node.type.name === 'image') {
      const $pos = view.state.doc.resolve(nodePos);
      if ($pos.parent.type.name === 'imageGroup') {
        setImageGroupPos($pos.before());
        return true;
      }
      if (!node.attrs.uploading) {
        setImageModal({
          open: true,
          pos: nodePos,
          src: node.attrs.src || '',
          alt: node.attrs.alt || '',
          caption: node.attrs.caption || '',
        });
        return true;
      }
    }
    return false;
  };

  handlersRef.current = { insertFiles: insertImagesFromFiles, clickOn };

  // Potlood- of randklik uit de node view van de groep.
  useEffect(() => {
    const onImageGroupEdit = (event: Event) => {
      const pos = (event as CustomEvent<{ pos: number }>).detail?.pos;
      if (typeof pos === 'number') setImageGroupPos(pos);
    };
    document.addEventListener('imageGroupEdit', onImageGroupEdit);
    return () => document.removeEventListener('imageGroupEdit', onImageGroupEdit);
  }, []);

  const closeImageModal = () => setImageModal(closedImageModal);

  const handleImageSave = () => {
    if (!editor || imageModal.pos === null) return;
    const attrs = {
      alt: imageModal.alt.trim() || null,
      caption: imageModal.caption.trim() || null,
    };
    if (!setImageAttrsAt(editor, imageModal.pos, attrs, imageModal.src)) {
      updateImageAttrsBySrc(editor, imageModal.src, attrs);
    }
    closeImageModal();
    onChanged();
    editor.commands.focus();
  };

  const handleImageRemove = () => {
    if (!editor || imageModal.pos === null) return;
    if (!removeImageAt(editor, imageModal.pos, imageModal.src)) {
      removeImageBySrc(editor, imageModal.src);
    }
    closeImageModal();
    onChanged();
    editor.commands.focus();
  };

  const closeImageGroupModal = () => {
    setImageGroupPos(null);
    onChanged();
    editor?.commands.focus();
  };

  const prepareForSave = (): boolean => {
    if (!editor) return true;
    if (pendingUploadsRef.current > 0) return false;
    removeBlobImages(editor);
    normalizeImageGroups(editor);
    return true;
  };

  return {
    openImagePicker: () => fileInputRef.current?.click(),
    openGroupPicker: () => {
      groupFileTargetRef.current = null;
      groupFileInputRef.current?.click();
    },
    pendingUploads: () => pendingUploadsRef.current,
    prepareForSave,
    toolsProps: {
      editor,
      fileInputRef,
      groupFileInputRef,
      onImageFiles: (files) => insertImagesFromFiles(files),
      onGroupFiles: (files) => insertImageGroupFromFiles(files, groupFileTargetRef.current),
      imageModal,
      setImageAlt: (value) => setImageModal((current) => ({ ...current, alt: value })),
      setImageCaption: (value) => setImageModal((current) => ({ ...current, caption: value })),
      onImageSave: handleImageSave,
      onImageRemove: handleImageRemove,
      onImageClose: closeImageModal,
      imageGroupPos,
      onGroupClose: closeImageGroupModal,
      onGroupAddFiles: (groupPos) => {
        groupFileTargetRef.current = groupPos;
        groupFileInputRef.current?.click();
      },
    },
  };
}
